import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { captureError } from "@/lib/error-message";

export interface Contact {
  id: string;
  first_name: string;
  last_name: string | null;
  // Nullable as of migration 20260805121937: GHL phone-only / SMS-only leads have
  // no email address, and refusing to store them made their conversations
  // permanently unsyncable (see ghl-poll's conversations arm).
  email: string | null;
  phone: string | null;
  ghl_contact_id: string | null;
  ghl_location_id: string | null;
  last_synced_at: string | null;
  // Square Customers linkage (#803). Set once the contact is pushed to Square.
  square_customer_id: string | null;
  square_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactWithStats extends Contact {
  quote_count: number;
  job_count: number;
  total_value: number;
}

// Cap the unbounded picker load. 500 covers most workflows; searches filter
// server-side via the .or() so older contacts can still be found by name/email.
const CONTACTS_PICKER_LIMIT = 500;

export function useContacts(search?: string) {
  return useQuery({
    queryKey: ["contacts", search],
    queryFn: async () => {
      let query = supabase
        .from("contacts")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(CONTACTS_PICKER_LIMIT);

      if (search) {
        query = query.or(
          `first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Contact[];
    },
  });
}

export interface PaginatedContacts {
  contacts: ContactWithStats[];
  totalCount: number;
}

const PAGE_SIZE = 50;

export function useContactsPaginated(search?: string, page: number = 0) {
  return useQuery({
    queryKey: ["contacts_with_stats", search, page],
    queryFn: async () => {
      // RPC defines search_term as DEFAULT NULL, so null is valid here.
      // Use a typed-any cast to avoid strict-null rejection on the generated
      // RPC argument type (which assumes the client always sends a value).
      const { data, error } = await supabase.rpc(
        "get_contacts_with_stats",
        {
          search_term: search || null,
          page_size: PAGE_SIZE,
          page_offset: page * PAGE_SIZE,
        } as unknown as { search_term: string; page_size: number; page_offset: number }
      );
      if (error) throw error;

      const rows = (data || []) as Array<ContactWithStats & { total_count: number }>;
      const totalCount = rows.length > 0 ? Number(rows[0].total_count) : 0;

      return {
        contacts: rows.map(({ total_count, ...rest }) => ({
          ...rest,
          quote_count: Number(rest.quote_count),
          job_count: Number(rest.job_count),
          total_value: Number(rest.total_value),
        })),
        totalCount,
      } as PaginatedContacts;
    },
  });
}

export function useContactStats() {
  return useQuery({
    queryKey: ["contact_stats"],
    queryFn: async () => {
      const [totalRes, syncedRes, settingsRes] = await Promise.all([
        supabase.from("contacts").select("*", { count: "exact", head: true }),
        supabase.from("contacts").select("*", { count: "exact", head: true }).not("ghl_contact_id", "is", null),
        supabase.from("app_settings").select("value").eq("key", "ghl_last_sync_at").maybeSingle(),
      ]);
      // supabase-js resolves failures rather than rejecting, so without these a
      // failed read shows up as 0 / 0 / "Never" on the Contacts page (#778).
      if (totalRes.error) throw totalRes.error;
      if (syncedRes.error) throw syncedRes.error;
      if (settingsRes.error) throw settingsRes.error;

      return {
        total: totalRes.count ?? 0,
        synced: syncedRes.count ?? 0,
        lastSyncAt: settingsRes.data?.value ?? null,
      };
    },
    staleTime: 30_000,
  });
}

export function useContactQuotes(contactId: string | null) {
  return useQuery({
    queryKey: ["contact_quotes", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("id, quote_number, client_name, status, pipeline_stage, total, amount_paid, event_date, service_type, created_at")
        .eq("contact_id", contactId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (contact: {
      first_name: string;
      last_name?: string;
      email: string;
      phone?: string;
    }) => {
      const { data, error } = await supabase
        .from("contacts")
        .insert(contact)
        .select()
        .single();
      if (error) throw error;

      // Fire-and-forget GHL sync with logging so silent sync failures surface
      // in console/Sentry rather than leaving the two systems silently desynced.
      supabase.functions.invoke("ghl-contacts", {
        body: { action: "sync-to-ghl", contactId: data.id },
      }).then(({ error: ghlErr }) => {
        // TODO(sentry): replace with Sentry.captureException once DSN is live everywhere.
        if (ghlErr) console.error("GHL sync failed for new contact", data.id, ghlErr);
      });

      return data as Contact;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contacts_with_stats"] });
      qc.invalidateQueries({ queryKey: ["contact_stats"] });
      toast({ title: "Contact created" });
    },
    onError: (err: Error) => {
      toast({ title: "Error creating contact", description: err.message, variant: "destructive" });
    },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Contact> & { id: string }) => {
      const { data, error } = await supabase
        .from("contacts")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;

      supabase.functions.invoke("ghl-contacts", {
        body: { action: "sync-to-ghl", contactId: id },
      }).then(({ error: ghlErr }) => {
        // TODO(sentry): replace with Sentry.captureException once DSN is live everywhere.
        if (ghlErr) console.error("GHL sync failed for updated contact", id, ghlErr);
      });

      return data as Contact;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contacts_with_stats"] });
      toast({ title: "Contact updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error updating contact", description: err.message, variant: "destructive" });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("contacts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contacts_with_stats"] });
      qc.invalidateQueries({ queryKey: ["contact_stats"] });
      toast({ title: "Contact deleted" });
    },
    onError: (err: Error) => {
      toast({ title: "Error deleting contact", description: err.message, variant: "destructive" });
    },
  });
}

export function useFindOrCreateContact() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      first_name: string;
      last_name?: string;
      email: string;
      phone?: string;
    }): Promise<Contact> => {
      if (input.email) {
        const { data: existing } = await supabase
          .from("contacts")
          .select("*")
          .eq("email", input.email.toLowerCase().trim())
          .maybeSingle();
        if (existing) return existing as Contact;
      }

      const { data, error } = await supabase
        .from("contacts")
        .insert({
          first_name: input.first_name,
          last_name: input.last_name || null,
          email: input.email.toLowerCase().trim(),
          phone: input.phone || null,
        })
        .select()
        .single();
      if (error) throw error;

      supabase.functions.invoke("ghl-contacts", {
        body: { action: "sync-to-ghl", contactId: data.id },
      }).then(({ error: ghlErr }) => {
        // TODO(sentry): replace with Sentry.captureException once DSN is live everywhere.
        if (ghlErr) console.error("GHL sync failed after upsert", data.id, ghlErr);
      });

      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contacts_with_stats"] });
      return data as Contact;
    },
  });
}

export function useSyncFromGhl() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      let totalImported = 0;
      let cursor: string | null = null;
      let done = false;

      while (!done) {
        const { data, error } = (await supabase.functions.invoke("ghl-contacts", {
          body: { action: "sync-from-ghl", cursor },
        })) as { data: { imported?: number; nextCursor?: string | null; done?: boolean } | null; error: Error | null };
        if (error) throw new Error(await edgeFunctionErrorMessage(error, "GHL sync failed"));
        totalImported += data?.imported ?? 0;
        cursor = data?.nextCursor ?? null;
        done = data?.done ?? true;
      }

      // Save last sync timestamp
      await supabase.from("app_settings").upsert(
        { key: "ghl_last_sync_at", value: new Date().toISOString() },
        { onConflict: "key" }
      );

      return { imported: totalImported };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contacts_with_stats"] });
      qc.invalidateQueries({ queryKey: ["contact_stats"] });
      qc.invalidateQueries({ queryKey: ["app_settings"] });
      toast({ title: "Sync complete", description: `${data.imported} contacts imported from GHL` });
    },
    onError: (err: Error) => {
      captureError(err, "contacts_ghl_sync", err.message);
      toast({ title: "Sync failed", description: err.message, variant: "destructive" });
    },
  });
}

/**
 * Square sync status for a single contact (#803). Reads only the two Square
 * columns straight from `contacts` by id — deliberately NOT off a passed-in
 * contact object (on the Contacts page that object comes from the
 * get_contacts_with_stats RPC, whose RETURNS TABLE omits these columns, and is
 * a click-time snapshot). Extending that RPC would force a DROP+CREATE that
 * resets its ACL — the #760 anon-PII-leak mechanism — so we self-fetch instead.
 */
export function useContactSquareStatus(contactId: string | null) {
  return useQuery({
    queryKey: ["contact_square_status", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("square_customer_id, square_synced_at")
        .eq("id", contactId!)
        .single();
      if (error) throw error;
      return data as { square_customer_id: string | null; square_synced_at: string | null };
    },
  });
}

export interface PushToSquareResult {
  success: boolean;
  squareCustomerId: string;
  action: "created" | "updated";
}

/**
 * Push a contact to Square Customers via the `square-customers` edge function
 * (#803). Idempotent server-side: re-pushing updates the existing customer
 * rather than creating a duplicate.
 */
export function usePushContactToSquare() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (contactId: string): Promise<PushToSquareResult> => {
      const { data, error } = (await supabase.functions.invoke("square-customers", {
        body: { contactId },
      })) as { data: PushToSquareResult | null; error: Error | null };
      if (error) throw new Error(await edgeFunctionErrorMessage(error, "Push to Square failed"));
      if (!data?.squareCustomerId) throw new Error("Square did not return a customer id");
      return data;
    },
    onSuccess: (data, contactId) => {
      qc.invalidateQueries({ queryKey: ["contact_square_status", contactId] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contacts_with_stats"] });
      qc.invalidateQueries({ queryKey: ["contact_detail", contactId] });
      toast({
        title: data.action === "created" ? "Pushed to Square" : "Updated in Square",
        description: "Square customer synced.",
      });
    },
    onError: (err: Error) => {
      captureError(err, "contact_push_square", err.message);
      toast({ title: "Push to Square failed", description: err.message, variant: "destructive" });
    },
  });
}
